/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { PreloadedQuery } from 'react-relay';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	RouteResource,
	RouterContext,
	RouterDataContext,
} from '@atlassian/react-resource-router';
import QUERY_NEW, {
	type TopNavigationQuery,
	type TopNavigationQuery$variables,
} from '@jira/navigation-apps__atlassian-navigation/src/ui/__generated__/TopNavigationQuery.graphql';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import {
	createRelayResource,
	RELAY_RESOURCE_TYPE,
} from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import type { ApplicationPermissions } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { getProjectKeyOrId } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';

const hasOnlyJSMAndCSMAccess = (appPermissions: ApplicationPermissions) => {
	const {
		hasServiceDeskAccess,
		hasCustomerServiceAccess,
		hasSoftwareAccess,
		hasProductDiscoveryAccess,
	} = appPermissions;

	return (
		hasServiceDeskAccess &&
		hasCustomerServiceAccess &&
		!hasSoftwareAccess &&
		!hasProductDiscoveryAccess
	);
};

/**
 * Derives the Relay operation variables the Jira top-navigation fragments need.
 *
 * Shared so the same derivation can feed both `topNav4UIStateResource` (the
 * standalone top-nav query) and the GlobalRoot query
 * (`useJiraAppChromeQuery`), which spreads `...TopNavigation_slotsQuery`
 * and therefore requires the same variables as root operation variables.
 *
 * Note: `isNavStateQuerySkipped` is only consumed by the standalone query's
 * `jira { navigationUIState }` block, which the GlobalRoot query does not
 * include; callers that don't need it can ignore it.
 */
export const getTopNavRelayVariables = (
	{ match, route }: RouterContext | RouterDataContext,
	{
		cloudId,
		isAnonymous,
		atlassianAccountId,
		appPermissions,
		appEditions,
	}: {
		cloudId: string;
		isAnonymous: boolean;
		atlassianAccountId: string | null;
		appPermissions: ApplicationPermissions;
		appEditions: { customerService: string };
	},
): TopNavigationQuery$variables => {
	const collabContextRoutingAri = fg('post-office_enable_routing_workspace_id_prop')
		? PlatformSiteAri.create({ siteId: cloudId }).toString()
		: undefined;
	const issueKey = match.params.issueKey ?? '';
	const projectKey = match.params.projectKey ?? '';
	const projectIdOrKey = getProjectKeyOrId(match);

	const isCsmRoute =
		match.path.startsWith('/jira/customer-service-management') ||
		match.path.startsWith('/jira/helpcenter') || // note: remove when cleaning csm_appification_standalone_navigation
		match.path.startsWith('/jira/customer-service'); // note: remove when cleaning csm_appification_standalone_navigation

	return {
		cloudId,
		isAnonymous,
		atlassianAccountId: atlassianAccountId ?? '',
		isNavStateQuerySkipped: isAnonymous || fg('navx-global-side-nav'),
		issueKey: issueKey ?? '',
		shouldCheckProjectTypeForIssue:
			route.group === 'issue' && Boolean(issueKey) && hasOnlyJSMAndCSMAccess(appPermissions),
		projectKey: projectKey ?? '',
		shouldCheckProjectType: Boolean(projectKey) && hasOnlyJSMAndCSMAccess(appPermissions),
		projectIdOrKey,
		shouldFetchProjectTypeForSettingsAndEA: fg('taiyo-137_exclude_anon_from_ea_project_type')
			? !isAnonymous && Boolean(projectIdOrKey)
			: Boolean(projectIdOrKey),
		// we can SSR csmLiveChatAgent status indicator
		shouldFetchCSMLiveChatAgent:
			appPermissions.hasCustomerServiceAccess &&
			appEditions.customerService === PREMIUM_EDITION &&
			isCsmRoute &&
			fg('csm-omni-channel-live-chat-in-widget'),
		// we can't SSR csmLiveChatAgent
		// let's get the project type to check if we should mount csmLiveChatAgent status indicator
		// note: remove when cleaning csm_appification_standalone_navigation
		shouldFetchProjectType:
			appPermissions.hasCustomerServiceAccess &&
			appEditions.customerService === PREMIUM_EDITION &&
			Boolean(projectIdOrKey) &&
			!isCsmRoute &&
			fg('csm-omni-channel-live-chat-in-widget'),

		skipHelpButtonRelayRead: isAnonymous,
		collabContextRoutingAri,
		// NAVX-5411: spike measuring the cost of fetching + rendering a unit
		// tile as the app switcher trigger icon.
		// NAVX-5411: the appSwitcher_unitProfile resolver's documented,
		// verified-working example uses the "confluence" resource owner
		// (https://hello.atlassian.net/wiki/spaces/navx/pages/7462283589).
		// This is a measurement spike, not a product-correctness concern, so
		// we deliberately match the documented example rather than guessing
		// at whether "jira" is in the resolver's supported product list.
		appSwitcherProductAri: `ari:cloud:confluence::site/${cloudId}`,
		skipAppSwitcherUnitProfile: !expVal('navx-5411-unit-icon-for-app-switcher', 'isEnabled', false),
	};
};

export const topNav4UIStateResource: RouteResource<
	PreloadedQuery<TopNavigationQuery, Record<string, unknown>>
> = createRelayResource<TopNavigationQuery>({
	type: `${RELAY_RESOURCE_TYPE}_TOPNAV_UI_STATE_RELAY`,
	getQuery: (routerContext, { tenantContext }) => {
		return {
			parameters: QUERY_NEW,
			variables: getTopNavRelayVariables(routerContext, tenantContext),
			options: { fetchPolicy: 'store-and-network' },
		};
	},
	isCritical: true,
});
